import React, { useEffect, useState } from 'react'
import { Alert, AlertTitle } from '@material-ui/lab'

import { getPageContent, updatePageContent, getPreSignedUrl, uploadImg } from '../api.js'

const cloudFrontURL = 'https://d24y9uuesrc84o.cloudfront.net'

// KPod editor for the Suburb guides.
// All suburbs are stored in a single `contents` row named "suburbs"
// (value = JSON object keyed by slug). The whole object is loaded, edited
// and saved back via PATCH. The row is seeded server-side, so the editor
// only ever PATCHes it (a POST creates a row the public API won't read back).

const slugify = (s) =>
  (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

// Resize an image file to a max width (keeping aspect) and re-encode as JPEG.
// Runs in the browser (KPod editor only). Falls back to the original file if
// anything goes wrong. Keeps hero images sharp at display size without shipping
// multi-megapixel originals the browser then downscales (which looks pixelated).
const resizeToJpeg = (file, maxWidth) => new Promise((resolve) => {
  try {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      const scale = Math.min(1, maxWidth / img.width)
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, 0, 0, w, h)
      canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', 0.85)
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file) }
    img.src = url
  } catch (e) {
    resolve(file)
  }
})

const blankSuburb = () => ({
  slug: '', name: '', region: 'Auckland', headline: '',
  metaTitle: '', metaDescription: '',
  intro: [''], whoBuysHere: '',
  propertyTypes: [''], priceRanges: [{ type: '', range: '' }], priceNote: '',
  schools: { primary: [''], intermediate: [], secondary: [''] },
  lifestyle: [''], sellingNow: '',
  faqs: [{ q: '', a: '' }], nearbySuburbs: [], images: ['', '', ''],
})

// ── small reusable field editors ─────────────────────────────────────────

const TextField = ({ label, value, onChange, textarea, rows }) => (
  <div className="column" style={{ marginBottom: '10px' }}>
    <label className="kpod-form-infotext-header">{label}</label>
    {textarea
      ? <textarea rows={rows || 3} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      : <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} />}
  </div>
)

// Editor for a list of strings (intro paragraphs, bullets, school lists…)
const StringList = ({ label, items, onChange, textarea }) => {
  const set = (i, v) => { const n = items.slice(); n[i] = v; onChange(n) }
  const add = () => onChange([...(items || []), ''])
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i))
  const move = (i, d) => {
    const j = i + d
    if (j < 0 || j >= items.length) return
    const n = items.slice();[n[i], n[j]] = [n[j], n[i]]; onChange(n)
  }
  return (
    <div className="column" style={{ marginBottom: '10px' }}>
      <label className="kpod-form-infotext-header">{label}</label>
      {(items || []).map((it, i) => (
        <div className="row" key={i} style={{ alignItems: 'center', marginBottom: '4px' }}>
          {textarea
            ? <textarea rows={2} style={{ flex: 1 }} value={it} onChange={(e) => set(i, e.target.value)} />
            : <input type="text" style={{ flex: 1 }} value={it} onChange={(e) => set(i, e.target.value)} />}
          <i className="fa fa-arrow-up functions-btn" title="Move up" style={iconStyle} onClick={() => move(i, -1)} />
          <i className="fa fa-arrow-down functions-btn" title="Move down" style={iconStyle} onClick={() => move(i, 1)} />
          <i className="fa fa-times-circle functions-btn" title="Remove" style={iconStyle} onClick={() => remove(i)} />
        </div>
      ))}
      <button className="kpod-text-button" type="button" onClick={add}>+ Add</button>
    </div>
  )
}

const iconStyle = { color: '#707070', cursor: 'pointer', padding: '4px', fontSize: '18px' }

const Suburbs = () => {
  const [suburbs, setSuburbs] = useState({})       // object keyed by slug
  const [order, setOrder] = useState([])           // slug order
  const [rowId, setRowId] = useState(null)
  const [editKey, setEditKey] = useState(null)     // slug being edited (original key)
  const [draft, setDraft] = useState(null)         // working copy
  const [error, setError] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [uploading, setUploading] = useState({}) // { [imageIndex]: 'uploading' | 'error' }

  useEffect(() => { getPageContent('suburbs', handleLoad, handleError) }, [])

  const handleLoad = (data) => {
    if (data && data.length) {
      setRowId(data[0].id)
      try {
        const obj = JSON.parse(data[0].value)
        setSuburbs(obj && typeof obj === 'object' ? obj : {})
        setOrder(Object.keys(obj || {}))
      } catch (e) { setSuburbs({}); setOrder([]) }
    } else { setRowId(null); setSuburbs({}); setOrder([]) }
  }

  const handleError = (err) => setError(err)

  const startNew = () => { setDraft(blankSuburb()); setEditKey(null); setSaveSuccess(false); setError(false) }
  const startEdit = (key) => {
    if (editKey === key) { setDraft(null); setEditKey(null); return } // toggle closed
    const s = suburbs[key]
    setDraft(JSON.parse(JSON.stringify({ ...blankSuburb(), ...s, images: [...(s.images || []), '', '', ''].slice(0, 3) })))
    setEditKey(key); setSaveSuccess(false); setError(false)
  }

  const upd = (field, value) => setDraft((p) => {
    const n = { ...p, [field]: value }
    // Slug auto-generates from the name for NEW suburbs. Existing suburbs keep
    // their slug (it's the public URL) so renaming the display name is safe.
    if (field === 'name' && !editKey) n.slug = slugify(value)
    return n
  })
  const updSchool = (tier, value) => setDraft((p) => ({ ...p, schools: { ...p.schools, [tier]: value } }))
  const updImage = (i, v) => setDraft((p) => { const im = [...(p.images || ['', '', ''])]; im[i] = v; return { ...p, images: im } })

  // Native upload: resize (client-side) then pre-signed PUT to S3, and store
  // the CloudFront URL. Suburb hero images render at most ~900px wide, so a
  // 1800px max keeps them retina-crisp while avoiding multi-MB originals being
  // downscaled by the browser (which looks pixelated and bloats the page).
  const uploadImage = (i, file) => {
    if (!file) return
    setUploading((u) => ({ ...u, [i]: 'uploading' }))
    resizeToJpeg(file, 1800).then((blob) => {
      const objectName = `${draft.slug || 'suburb'}-${Date.now()}.jpg`
      getPreSignedUrl(
        {
          id: 1,
          idType: 'suburbs',
          objectName,
          objectType: 'images',
          objectContentType: 'image/jpeg',
        },
        (res) => {
          uploadImg(
            blob, 'jpeg', res.preSignedURL,
            () => {
              updImage(i, `${cloudFrontURL}/suburbs/1/images/${objectName}`)
              setUploading((u) => ({ ...u, [i]: undefined }))
            },
            () => setUploading((u) => ({ ...u, [i]: 'error' }))
          )
        },
        () => setUploading((u) => ({ ...u, [i]: 'error' }))
      )
    })
  }

  const removeSuburb = (key) => {
    const next = { ...suburbs }; delete next[key]
    const nextOrder = order.filter((k) => k !== key)
    setSuburbs(next); setOrder(nextOrder)
    if (key === editKey) { setDraft(null); setEditKey(null) }
    persist(next, nextOrder)
  }

  const saveSuburb = () => {
    setError(false)
    if (!draft.name || !draft.slug) { setError({ error_description: 'Name and slug are required.' }); return }
    if (draft.slug !== editKey && suburbs[draft.slug]) {
      setError({ error_description: `Slug "${draft.slug}" is already used by another suburb.` }); return
    }
    // strip empty rows from the arrays before saving
    const clean = {
      ...draft,
      intro: draft.intro.filter((x) => x.trim()),
      propertyTypes: draft.propertyTypes.filter((x) => x.trim()),
      lifestyle: draft.lifestyle.filter((x) => x.trim()),
      nearbySuburbs: draft.nearbySuburbs.filter((x) => x.trim()),
      priceRanges: draft.priceRanges.filter((p) => p.type.trim() || p.range.trim()),
      faqs: draft.faqs.filter((f) => f.q.trim() || f.a.trim()),
      schools: {
        primary: draft.schools.primary.filter((x) => x.trim()),
        intermediate: draft.schools.intermediate.filter((x) => x.trim()),
        secondary: draft.schools.secondary.filter((x) => x.trim()),
      },
      images: (draft.images || []).map((x) => (x || '').trim()).filter(Boolean),
    }
    const next = { ...suburbs }
    if (editKey && editKey !== clean.slug) delete next[editKey] // slug renamed
    next[clean.slug] = clean
    const nextOrder = order.includes(clean.slug) ? order : [...order.filter((k) => k !== editKey), clean.slug]
    setSuburbs(next); setOrder(nextOrder); setEditKey(clean.slug)
    persist(next, nextOrder)
  }

  // Persist the whole object (in display order) to the `suburbs` contents row
  const persist = (obj, ord) => {
    if (!rowId) {
      setError({ error_description: 'The "suburbs" content record does not exist yet. Ask a developer to create it before publishing.' })
      return
    }
    const ordered = {}; ord.forEach((k) => { if (obj[k]) ordered[k] = obj[k] })
    updatePageContent(
      rowId,
      { name: 'suburbs', type: 'data', value: JSON.stringify(ordered), storageKey: null },
      () => setSaveSuccess(true),
      handleError
    )
  }

  return (
    <div className="kpod-container">
      <h1 className="kpod-main-title">Suburb Guides</h1>

      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="kpod-infotext" style={{ marginRight: '24px' }}>
          Guides published here appear at <strong>/suburbs</strong>. Changes save the whole list;
          avoid two people editing at once. Add up to 3 image URLs per suburb.
        </div>
        <button className="kpod-form-button" onClick={startNew} style={{ whiteSpace: 'nowrap' }}>+ Add suburb</button>
      </div>

      <div className="column">
        {/* List (shown when not editing) */}
        {!draft && (order.length === 0
          ? <div className="kpod-infotext">No suburbs yet.</div>
          : (
            <div className="column kpod-insight-list">
              {order.map((key) => suburbs[key] && (
                <div key={key}
                  className="row kpod-insight-item"
                  onClick={() => startEdit(key)}>
                  <div className="column" style={{ flex: 1 }}>
                    <div className="kpod-insight-title">{suburbs[key].name || '(unnamed)'}</div>
                    <div className="kpod-insight-meta">{suburbs[key].region} &middot; /suburbs/{key}</div>
                  </div>
                  <i className="fa fa-times-circle functions-btn" title="Delete suburb"
                    style={{ color: '#707070', cursor: 'pointer', padding: '4px' }}
                    onClick={(e) => { e.stopPropagation(); removeSuburb(key) }} />
                </div>
              ))}
            </div>
          ))}

        {/* Editor (replaces the list when a suburb is selected) */}
        {draft && (
          <div className="column kpod-form-outer" style={{ marginTop: '4px' }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <button className="kpod-text-button" type="button" onClick={() => { setDraft(null); setEditKey(null) }}>
                ← Back to suburbs
              </button>
              <strong style={{ color: '#5e2351' }}>{editKey ? draft.name : 'New suburb'}</strong>
            </div>
            <TextField label="Name" value={draft.name} onChange={(v) => upd('name', v)} />
            <div className="column" style={{ marginBottom: '10px' }}>
              <label className="kpod-form-infotext-header">Page URL (auto-generated)</label>
              <div className="kpod-infotext">
                /suburbs/<strong>{draft.slug || '…'}</strong>
              </div>
            </div>
            <TextField label="Region" value={draft.region} onChange={(v) => upd('region', v)} />
            <TextField label="Headline" value={draft.headline} onChange={(v) => upd('headline', v)} textarea rows={2} />
            <TextField label="Meta title (SEO)" value={draft.metaTitle} onChange={(v) => upd('metaTitle', v)} />
            <TextField label="Meta description (SEO)" value={draft.metaDescription} onChange={(v) => upd('metaDescription', v)} textarea rows={2} />

            <label className="kpod-form-infotext-header">Images (up to 3)</label>
            <div className="kpod-infotext" style={{ marginBottom: '6px' }}>
              Upload a file or paste an image URL. Shown across the top of the suburb page in order.
              Uploads are automatically resized and optimised for the web.
            </div>
            <div className="row" style={{ flexWrap: 'wrap', marginBottom: '10px' }}>
              {[0, 1, 2].map((i) => (
                <div className="column kpod-suburb-image-slot" key={i}>
                  <div className="kpod-suburb-image-preview">
                    {draft.images[i]
                      ? <img src={draft.images[i]} alt="" onError={(e) => { e.target.style.display = 'none' }} />
                      : <span className="kpod-suburb-image-empty">No image</span>}
                  </div>
                  <input type="text" placeholder="Image URL" value={draft.images[i] || ''}
                    onChange={(e) => updImage(i, e.target.value)} />
                  <div className="row" style={{ alignItems: 'center', marginTop: '4px' }}>
                    <label className="kpod-text-button" style={{ margin: 0 }}>
                      {uploading[i] === 'uploading' ? 'Uploading…' : 'Choose file'}
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={(e) => uploadImage(i, e.target.files[0])} />
                    </label>
                    {draft.images[i] && (
                      <i className="fa fa-times-circle functions-btn" title="Remove image"
                        style={iconStyle} onClick={() => updImage(i, '')} />
                    )}
                  </div>
                  {uploading[i] === 'error' && (
                    <div style={{ color: '#c0392b', fontSize: '12px' }}>Upload failed — try again.</div>
                  )}
                </div>
              ))}
            </div>

            <StringList label="Overview paragraphs" items={draft.intro} onChange={(v) => upd('intro', v)} textarea />
            <TextField label="Who buys here" value={draft.whoBuysHere} onChange={(v) => upd('whoBuysHere', v)} textarea rows={4} />

            <StringList label="Property types" items={draft.propertyTypes} onChange={(v) => upd('propertyTypes', v)} />

            <label className="kpod-form-infotext-header">Price ranges</label>
            {draft.priceRanges.map((p, i) => (
              <div className="row" key={i} style={{ alignItems: 'center', marginBottom: '4px' }}>
                <input type="text" placeholder="Type" style={{ flex: 1 }} value={p.type}
                  onChange={(e) => { const n = draft.priceRanges.slice(); n[i] = { ...n[i], type: e.target.value }; upd('priceRanges', n) }} />
                <input type="text" placeholder="Indicative range" style={{ flex: 1, marginLeft: '6px' }} value={p.range}
                  onChange={(e) => { const n = draft.priceRanges.slice(); n[i] = { ...n[i], range: e.target.value }; upd('priceRanges', n) }} />
                <i className="fa fa-times-circle functions-btn" style={iconStyle}
                  onClick={() => upd('priceRanges', draft.priceRanges.filter((_, idx) => idx !== i))} />
              </div>
            ))}
            <button className="kpod-text-button" type="button" onClick={() => upd('priceRanges', [...draft.priceRanges, { type: '', range: '' }])}>+ Add price row</button>
            <TextField label="Price note" value={draft.priceNote} onChange={(v) => upd('priceNote', v)} textarea rows={2} />

            <StringList label="Schools — Primary" items={draft.schools.primary} onChange={(v) => updSchool('primary', v)} />
            <StringList label="Schools — Intermediate" items={draft.schools.intermediate} onChange={(v) => updSchool('intermediate', v)} />
            <StringList label="Schools — Secondary" items={draft.schools.secondary} onChange={(v) => updSchool('secondary', v)} />

            <StringList label="Lifestyle & amenities" items={draft.lifestyle} onChange={(v) => upd('lifestyle', v)} />
            <TextField label="What's selling now" value={draft.sellingNow} onChange={(v) => upd('sellingNow', v)} textarea rows={2} />

            <label className="kpod-form-infotext-header">FAQs</label>
            {draft.faqs.map((f, i) => (
              <div className="column" key={i} style={{ marginBottom: '8px', borderLeft: '2px solid #e0d8de', paddingLeft: '8px' }}>
                <div className="row" style={{ alignItems: 'center' }}>
                  <input type="text" placeholder="Question" style={{ flex: 1 }} value={f.q}
                    onChange={(e) => { const n = draft.faqs.slice(); n[i] = { ...n[i], q: e.target.value }; upd('faqs', n) }} />
                  <i className="fa fa-times-circle functions-btn" style={iconStyle}
                    onClick={() => upd('faqs', draft.faqs.filter((_, idx) => idx !== i))} />
                </div>
                <textarea rows={3} placeholder="Answer" value={f.a}
                  onChange={(e) => { const n = draft.faqs.slice(); n[i] = { ...n[i], a: e.target.value }; upd('faqs', n) }} />
              </div>
            ))}
            <button className="kpod-text-button" type="button" onClick={() => upd('faqs', [...draft.faqs, { q: '', a: '' }])}>+ Add FAQ</button>

            <label className="kpod-form-infotext-header">Nearby suburb guides</label>
            {draft.nearbySuburbs.map((sl) => (
              <div className="row" key={sl} style={{ alignItems: 'center', marginBottom: '4px' }}>
                <span style={{ flex: 1 }}>{suburbs[sl] ? suburbs[sl].name : sl}</span>
                <i className="fa fa-times-circle functions-btn" title="Remove" style={iconStyle}
                  onClick={() => upd('nearbySuburbs', draft.nearbySuburbs.filter((x) => x !== sl))} />
              </div>
            ))}
            <select value="" style={{ maxWidth: '320px' }}
              onChange={(e) => { if (e.target.value) upd('nearbySuburbs', [...draft.nearbySuburbs, e.target.value]) }}>
              <option value="">+ Add nearby suburb…</option>
              {order
                .filter((k) => k !== editKey && suburbs[k] && !draft.nearbySuburbs.includes(k))
                .map((k) => <option key={k} value={k}>{suburbs[k].name}</option>)}
            </select>

            <div className="row right" style={{ marginTop: '12px' }}>
              {error && error.error_description && (
                <Alert severity="error"><AlertTitle className="alert-title">{error.error_description}</AlertTitle></Alert>
              )}
              {saveSuccess && (
                <Alert severity="success"><AlertTitle className="alert-title">Saved</AlertTitle></Alert>
              )}
              <button className="kpod-form-button" onClick={saveSuburb}>Save</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Suburbs
